import { z } from 'zod'

/**
 * A arte de um produto da loja: o que e um nome de arquivo valido, e o que e um arquivo aceito.
 * Fonte da verdade: docs/adr/0021-o-armazem-de-imagens.md, docs/06 secao 4.1 e 4.4.
 *
 * ## Por que isto e contrato, e nao detalhe do `apps/api`
 *
 * O nome do arquivo atravessa TRES processos: o `apps/api` o produz, o `apps/checkout` o guarda
 * como referencia opaca, e o `apps/web2` o renderiza. Duas copias divergiriam, e a divergencia
 * seria um arquivo aceito de um lado e recusado do outro — defeito que so aparece em producao.
 *
 * O `apps/admin-web` NAO importa daqui, e a ausencia e a ADR 0009: o painel nao depende do
 * monorepo. Ele duplica o teto e a lista de formatos, como ja duplica o `packDraftSchema` inteiro.
 * Se um numero daqui mudar, o de la muda junto — ha comentario nos dois lados apontando um para o
 * outro.
 *
 * ## Puro de proposito
 *
 * Nada de `node:*` aqui: a deteccao opera sobre `Uint8Array`, e o sha-256 fica em cada servico com
 * `node:crypto`. E o que mantem este modulo importavel pelo `web2`, que roda no navegador.
 */

/**
 * O teto de um arquivo de arte.
 *
 * 512 KiB e quatro vezes a maior imagem que o jogo ja serve — a arte de carta tem ~120 KB —, e
 * mantem a vitrine inteira (dois ou tres pacotes mais seis pacotes de gema) abaixo de alguns
 * megabytes. Acima disso o problema deixa de ser de arte e passa a ser de quem exportou o arquivo.
 */
export const PRODUCT_IMAGE_MAX_BYTES = 512 * 1024

/**
 * Os formatos aceitos, com a assinatura que os identifica NOS BYTES.
 *
 * **SVG esta fora, e nao e preferencia de formato: e XSS.** Um SVG servido pela origem do `api`
 * executa o `<script>` que estiver dentro dele, com aquela origem. GIF esta fora por produto:
 * imagem animada ao lado de uma tabela de probabilidade compete com o que docs/06 secao 4.4 obriga
 * a mostrar.
 *
 * O WebP e o unico com assinatura partida — `RIFF` nos bytes 0-3 e `WEBP` nos bytes 8-11, com o
 * tamanho no meio —, e por isso a assinatura e uma lista de pares (deslocamento, bytes) em vez de
 * um prefixo.
 */
export const PRODUCT_IMAGE_FORMATS = [
  {
    mime: 'image/png',
    extension: 'png',
    signature: [{ offset: 0, bytes: [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a] }],
  },
  { mime: 'image/jpeg', extension: 'jpg', signature: [{ offset: 0, bytes: [0xff, 0xd8, 0xff] }] },
  {
    mime: 'image/webp',
    extension: 'webp',
    // 'RIFF' ... 'WEBP'
    signature: [
      { offset: 0, bytes: [0x52, 0x49, 0x46, 0x46] },
      { offset: 8, bytes: [0x57, 0x45, 0x42, 0x50] },
    ],
  },
] as const satisfies ReadonlyArray<{
  mime: string
  extension: string
  signature: ReadonlyArray<{ offset: number; bytes: readonly number[] }>
}>

export type ProductImageFormat = (typeof PRODUCT_IMAGE_FORMATS)[number]

/**
 * Os tipos que o cliente pode declarar. Declarar nao basta — quem decide sao os bytes.
 *
 * Tipada como `readonly string[]`, e nao como a uniao dos tres literais: quem a usa esta sempre
 * comparando com um cabecalho HTTP, que e `string`. A uniao obrigaria um `as` em cada chamada, e um
 * `as` numa comparacao de seguranca e onde o proximo engano se esconde.
 */
export const PRODUCT_IMAGE_MIMES: readonly string[] = PRODUCT_IMAGE_FORMATS.map((formato) => formato.mime)

/**
 * O nome de um arquivo de arte: o sha-256 do conteudo, mais a extensao do formato detectado.
 *
 * **A guarda contra path traversal e esta linha, e ela e uma allowlist.** Nome com `/`, `\`, `..`,
 * byte nulo ou unicode homografo nao e recusado: ele nao e REPRESENTAVEL. E mais forte que a
 * denylist que `apps/web2/app/card-art/[name]/route.ts` precisa manter, e e de graca — porque o
 * nome nunca veio do usuario.
 *
 * O sha-256 tambem e o que faz `cache-control: immutable` ser literalmente verdade: bytes
 * diferentes, nome diferente.
 */
export const productImageNameSchema = z.string().regex(/^[0-9a-f]{64}\.(png|jpg|webp)$/)

/**
 * Que formato sao estes bytes? `null` quando nao e nenhum dos aceitos.
 *
 * **O `content-type` que o cliente declarou nao entra aqui, de proposito.** Ele e afirmacao de quem
 * envia; os primeiros bytes sao o arquivo. Um `.svg` renomeado para `.png`, com o `content-type`
 * trocado junto, passa por toda checagem de metadado e morre nesta funcao — que e o caso que ela
 * existe para pegar.
 */
export function sniffProductImage(bytes: Uint8Array): ProductImageFormat | null {
  return (
    PRODUCT_IMAGE_FORMATS.find((formato) =>
      formato.signature.every(
        (parte) =>
          bytes.length >= parte.offset + parte.bytes.length &&
          parte.bytes.every((esperado, indice) => bytes[parte.offset + indice] === esperado),
      ),
    ) ?? null
  )
}

/** A extensao de um nome ja validado. Usada para decidir o `content-type` de quem serve. */
export function productImageMimeOf(name: string): string | null {
  const extensao = name.slice(name.lastIndexOf('.') + 1)
  return PRODUCT_IMAGE_FORMATS.find((formato) => formato.extension === extensao)?.mime ?? null
}
